import { useState } from "react";
import { useApprovalActions, ApprovalRequestType } from "@/hooks/useApprovalSystem";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, HelpCircle, ArrowRight } from "lucide-react";
import { ApprovalForwardModal } from "./ApprovalForwardModal";

interface Props {
  assigneeId: string;
  requestId: string;
  requestType: ApprovalRequestType;
  selectedLineIds?: Set<string>;
  totalLineCount?: number;
}

type ModalType = "approve" | "reject" | "info" | "forward" | null;

export const ApprovalActionBar = ({
  assigneeId,
  requestId,
  requestType,
  selectedLineIds,
  totalLineCount = 0,
}: Props) => {
  const [modal, setModal] = useState<ModalType>(null);
  const [note, setNote] = useState("");
  const { decide, forward } = useApprovalActions();

  const hasLines = totalLineCount > 0;
  const selectedCount = selectedLineIds?.size ?? 0;
  const isPartial = hasLines && selectedCount > 0 && selectedCount < totalLineCount;
  const noneSelected = hasLines && selectedCount === 0;
  const declinedCount = totalLineCount - selectedCount;

  const handleDecision = async (status: "approved" | "rejected" | "info_requested") => {
    if (status === "rejected" && !note.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    // Build metadata for partial approvals
    let finalNote = note.trim() || undefined;
    let metadata: Record<string, any> | undefined;

    if (hasLines && selectedLineIds) {
      const approvedIds = Array.from(selectedLineIds);
      // We don't have all line IDs here, but we pass what we have
      metadata = {
        approved_line_ids: approvedIds,
        approved_count: approvedIds.length,
        total_lines: totalLineCount,
      };
      if (isPartial && status === "approved") {
        const partialLabel = `Partial approval: ${approvedIds.length} of ${totalLineCount} line items approved`;
        finalNote = finalNote ? `${partialLabel}\n${finalNote}` : partialLabel;
      }
    }

    try {
      await decide.mutateAsync({
        assigneeId,
        requestId,
        status,
        note: finalNote,
      });
      const labels: Record<string, string> = {
        approved: isPartial ? "Partial Approval Submitted" : "Request Approved",
        rejected: "Request Rejected",
        info_requested: "Information Requested",
      };
      toast.success(labels[status], { duration: 5000 });
      setModal(null);
      setNote("");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const approveLabel = isPartial
    ? `Approve Selected (${selectedCount})`
    : "Approve";

  const declineLabel = isPartial
    ? `Decline Unchecked (${declinedCount})`
    : "Reject";

  return (
    <>
      <div className="border-t p-4 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Your Decision</p>
        {noneSelected && hasLines && (
          <p className="text-xs text-destructive">Select at least one line item to approve</p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => { setNote(""); setModal("approve"); }}
            className="gap-1"
            disabled={noneSelected}
          >
            <Check className="h-4 w-4" /> {approveLabel}
          </Button>
          <Button
            variant="outline"
            onClick={() => { setNote(""); setModal("reject"); }}
            className="gap-1"
          >
            <X className="h-4 w-4" /> {isPartial ? declineLabel : "Reject"}
          </Button>
          <Button variant="outline" onClick={() => { setNote(""); setModal("info"); }} className="gap-1">
            <HelpCircle className="h-4 w-4" /> Request Info
          </Button>
          <Button variant="outline" onClick={() => setModal("forward")} className="gap-1">
            <ArrowRight className="h-4 w-4" /> Forward
          </Button>
        </div>
      </div>

      {/* Approve Modal */}
      <Dialog open={modal === "approve"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isPartial ? "Partial Approval" : "Approve Request"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {isPartial
              ? `Approving ${selectedCount} of ${totalLineCount} line items. Unchecked items will be noted as declined.`
              : "Approving applies this change immediately and notifies the requester."}
          </p>
          <Textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => handleDecision("approved")} disabled={decide.isPending}>
              <Check className="h-4 w-4 mr-1" /> {isPartial ? `Confirm Partial Approval` : "Confirm Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={modal === "reject"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isPartial ? "Decline Unchecked Items" : "Reject Request"}</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Rejection reason (required)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDecision("rejected")} disabled={decide.isPending}>
              <X className="h-4 w-4 mr-1" /> Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Request Modal */}
      <Dialog open={modal === "info"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request More Information</DialogTitle></DialogHeader>
          <Textarea
            placeholder="What do you need from the requester?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => handleDecision("info_requested")} disabled={decide.isPending}>
              <HelpCircle className="h-4 w-4 mr-1" /> Send Information Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward Modal */}
      {modal === "forward" && (
        <ApprovalForwardModal
          open
          onClose={() => setModal(null)}
          assigneeId={assigneeId}
          requestId={requestId}
          requestType={requestType}
        />
      )}
    </>
  );
};
